import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Corner, Root } from '@radix-ui/react-scroll-area';
import {
  faAlbumCollectionCircleUser,
  faHeadphones,
  faListMusic,
  faMusic,
  faUserMusic,
} from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { styled } from '@components-shared/src/stitches';
import {
  ScrollViewport,
  StyledScrollbar,
  StyledThumb,
} from '@components/main-components/containers/RadixScroll';
import { DescriptionSelector } from '@components/main-components/selectors/description-selector';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import type { TopChartsType } from '@pages-shared/topCharts';
import { TopChartsTypeValues } from '@pages-shared/topCharts';
import type { ViralChartsType } from '@pages-shared/viralCharts';
import { ViralChartsTypeValues } from '@pages-shared/viralCharts';
import type { ChartTabsType } from '../ChartTabs';

const Icon = ({ content }: { content: TopChartsType | ViralChartsType }) => {
  if (content === 'sounds') {
    return <FontAwesomeIcon icon={faMusic} />;
  }

  if (content === 'artists') {
    return <FontAwesomeIcon icon={faUserMusic} />;
  }

  if (content === 'curators') {
    return <FontAwesomeIcon icon={faAlbumCollectionCircleUser} />;
  }

  if (content === 'playlists') {
    return <FontAwesomeIcon icon={faListMusic} />;
  }

  return <FontAwesomeIcon icon={faHeadphones} />;
};

export function ChartContentTabs({
  selected,
  loading,
  onSelection,
  chartType,
}: {
  selected: TopChartsType | ViralChartsType;
  onSelection: ((value: TopChartsType) => void) | ((value: ViralChartsType) => void);
  loading?: boolean;
  chartType: ChartTabsType;
}) {
  return (
    <StyledScrollArea>
      <StyledScrollViewport>
        <Container>
          {loading ? (
            <>
              <SelectorSkeleton />
              <SelectorSkeleton />
              <SelectorSkeleton />
            </>
          ) : (
            (chartType === 'viral' ? ViralChartsTypeValues : TopChartsTypeValues).map(content => {
              return (
                <StyledDescriptionSelector
                  key={content}
                  selected={selected === content}
                  // @ts-expect-error: Can't figure out how to get this type to work. Ticketing to follow up https://linear.app/sound/issue/ENG-7594/fix-typescript-issue-in-contenttabs
                  onClick={() => onSelection(content)}
                >
                  <Flex>
                    <Icon content={content} />
                    <span>{content}</span>
                  </Flex>
                </StyledDescriptionSelector>
              );
            })
          )}
        </Container>
        <StyledScrollbar hiddenOnMobile orientation="horizontal">
          <StyledThumb hiddenOnMobile />
        </StyledScrollbar>
        <Corner />
      </StyledScrollViewport>
    </StyledScrollArea>
  );
}

const SelectorSkeleton = styled(Skeleton, {
  borderRadius: 6,
  padding: '8px 16px',
  width: '100%',
  maxWidth: 309,
  minWidth: 150,
  height: 36,
});

const StyledScrollArea = styled(Root, {
  overflow: 'hidden',
  marginLeft: -12,
  marginRight: -20,

  '@tablet': {
    marginRight: 0,
  },
});

const StyledScrollViewport = styled(ScrollViewport, {
  paddingTop: 4,
  paddingBottom: 12,
  paddingLeft: 12,
  paddingRight: 12,
});

const Container = styled('div', {
  display: 'flex',
  alignItems: 'center',
  paddingY: 8,
  gap: 12,

  '@tablet': {
    gap: 16,
  },
});

const Flex = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  paddingLeft: 8,
});

const StyledDescriptionSelector = styled(DescriptionSelector, {
  width: '100%',
  maxWidth: 'calc(25% - 16px)',
  minWidth: 150,
});
